import { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { NotificationItemDTO, NotificationItemDTOCategory } from '@/api';
import BellIcon from '@/assets/icons/BellIcon.svg?react';
import { groupNotificationsByCategory } from '@/utils/functions/groupNotificationsByCategory/groupNotificationsByCategory';

import { NotificationCard } from './NotificationCard';
import styles from './styles.module.css';

import type { KnownCategory } from '@/utils/functions/groupNotificationsByCategory/groupNotificationsByCategory';

interface NotificationPanelProps {
  isOpen: boolean;
  onCloseClick: () => void;
  onNativeClose: () => void;
  notifications?: NotificationItemDTO[];
  isLoading?: boolean;
}

const SECTION_LABEL_KEY: Record<KnownCategory, string> = {
  [NotificationItemDTOCategory.GENERAL_INFORMATIONS]: 'notificationCenter.sections.generalInformations',
  [NotificationItemDTOCategory.OFFERS]: 'notificationCenter.sections.offers',
  [NotificationItemDTOCategory.MY_OPERATIONS]: 'notificationCenter.sections.myOperations',
};

const NotificationPanel = ({
  isOpen,
  onCloseClick: closeNotificationPanel,
  onNativeClose: closePanel,
  notifications,
  isLoading,
}: NotificationPanelProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const groupedNotifications = groupNotificationsByCategory(notifications ?? []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleBackdropClick = (e: MouseEvent) => {
      const rect = dialog.getBoundingClientRect();
      const isInsideElement =
        e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!isInsideElement) {
        closeNotificationPanel();
      }
    };

    dialog.addEventListener('click', handleBackdropClick);
    return () => {
      dialog.removeEventListener('click', handleBackdropClick);
    };
  }, [closeNotificationPanel]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      dialog.showModal();
    } else if (dialog.open) {
      return () => {
        dialog.close();
      };
    }
  }, [isOpen]);

  let panelContent;
  if (isLoading) {
    panelContent = <NotificationCard title="" message="" isLoading />;
  } else if (notifications?.length === 0) {
    panelContent = (
      <div className={styles.notificationCardEmpty}>
        <BellIcon className={styles.emptyStateIcon} aria-hidden="true" />
        <h3>{t('notificationCenter.noNotificationsTitle')}</h3>
        <p>{t('notificationCenter.noNotificationsMessage')}</p>
      </div>
    );
  } else {
    panelContent = (
      <div className={styles.notificationPanelBody}>
        <hr className={styles.notificationDivider} />
        {groupedNotifications.flatMap(({ category, notifications: categoryNotifications }, sectionIndex) => [
          ...(sectionIndex > 0 ? [<hr key={`${category}-divider`} className={styles.notificationDivider} />] : []),
          <section key={category} className={styles.notificationSection} aria-label={t(SECTION_LABEL_KEY[category])}>
            <h3 className={styles.notificationSectionTitle}>{t(SECTION_LABEL_KEY[category])}</h3>
            {categoryNotifications
              .slice()
              .reverse()
              .flatMap((notification, index) => [
                ...(index > 0
                  ? [<hr key={`${notification.id}-divider`} className={styles.notificationDivider} />]
                  : []),
                <NotificationCard
                  key={notification.id}
                  title={notification.title}
                  message={notification.message}
                  link={notification.link}
                  linkLabel={notification.linkLabel}
                  openInNewTab={notification.openInNewTab}
                  creationDate={notification.creationDate}
                  status={notification.status}
                  category={notification.category}
                />,
              ])}
          </section>,
        ])}
      </div>
    );
  }

  return (
    <dialog
      ref={dialogRef}
      className={styles.notificationPanel + (isOpen ? '' : ` ${styles.closing}`)}
      aria-labelledby="notification-panel-title"
      onAnimationEnd={(e) => {
        if (e.animationName.includes('slideOut') || e.animationName.includes('backdropFadeOut')) {
          e.currentTarget.classList.remove(styles.closing);
          e.currentTarget.close();
        }
      }}
      onClose={() => {
        closePanel();
      }}
      onCancel={(e) => {
        e.preventDefault();
        closeNotificationPanel();
      }}
    >
      <div className={styles.notificationPanelHeader}>
        <h2 id="notification-panel-title">{t('notificationCenter.title')}</h2>
        <button onClick={closeNotificationPanel} aria-label={t('notificationCenter.closeButtonLabel')}>
          <i className="totem-icon totem-icon-cross-big" />
        </button>
      </div>

      {panelContent}
    </dialog>
  );
};

export { NotificationPanel };
